import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { parseISO } from "date-fns";

import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import { useGetShiftDiscoveryDefaultDateRange } from "../../utils/useGetShiftDiscoveryDefaultDateRange";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { isBonusEligibleShift } from "./isBonusEligibleShift";

interface UseBonusEligibleShiftShoppingDataProps {
  minimumPostedHours: number;
}

export function useBonusEligibleShiftShoppingData(props: UseBonusEligibleShiftShoppingDataProps) {
  const { minimumPostedHours } = props;

  const { dates: dateStrings, distance, includedStates } = useShiftDiscoveryUserFiltersContext();
  const dates = dateStrings.map((dateString) => parseISO(dateString));

  const workerGeoLocation = useWorkerGeoLocation();
  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const { shifts, pagination, ...rest } = useGetOpenShiftsForDates({
    dates,
    dateRange: defaultDateRange,
    filter: {
      area: {
        latitude: workerGeoLocation?.latitude ?? 0,
        longitude: workerGeoLocation?.longitude ?? 0,
        radiusInMiles: distance,
      },
      isUrgent: false,
      ...(includedStates?.length && { includedStates }),
    },
    enabled: isDefined(workerGeoLocation),
  });

  const bonusEligibleShifts = shifts.filter((shift) => {
    const createdAt =
      "createdAt" in shift.attributes && typeof shift.attributes.createdAt === "string"
        ? shift.attributes.createdAt
        : undefined;

    return isBonusEligibleShift({
      createdAt,
      minimumPostedHours,
    });
  });

  return {
    ...rest,
    shifts: bonusEligibleShifts,
    allShifts: shifts,
    pagination,
  };
}
